/**
 * @module utmParams
 */

/**
 * Helper method to get and return the search (query) parameters from the specified url/path.
 *
 * @param {string} url - The url value, which can be a full url or just a path.
 *
 * @returns {(undefined|string)} The search parameters string, if the provided url contains search (query) parameters. Otherwise, undefined is returned.
 */
function getSearchParamsFrom(url) {
  const regex = /^[^?#]+\?([^#]+)/;
  return regex.exec(url) && regex.exec(url).length
    ? regex.exec(url)[1]
    : undefined;
}

/**
 * Helper method to extract the fragment (hash) from the specified URL.
 *
 * @param {string} url - The url value from which the fragment will be extracted.
 *
 * @returns {FragmentExtractData} - A fragment extract data object containing attributes for the fragment and the url without the fragment.
 */
function extractFragment(url) {
  const defaultFragment = '';
  const defaultUrl = '';

  if (typeof url !== 'string') {
    return { fragment: defaultFragment, url: defaultUrl };
  }

  const fragmentIndex = url.indexOf('#');

  if (fragmentIndex !== -1) {
    const urlWithoutFragment = url.slice(0, fragmentIndex);
    const fragment = url.slice(fragmentIndex);
    return {
      fragment,
      url: urlWithoutFragment,
    };
  }

  return { fragment: defaultFragment, url };
}

/**
 * Convenience function that will replace all UTM params in the specified
 * element URL with the UTM params found in the page URL (window.location.href).
 * If no UTM params are present in the page URL, the specified element URL is
 * left as-is and returned.
 *
 * Note: The priority is UTM params found on the page URL replacing any UTM
 * params on the specified element URL. If no UTM params are present in the page
 * URL, the specified element URL is left as-is and returned.
 *
 * @param {string} url - The internal url to which to append the utm params.
 * @param {object} windowLocation - The window location object from where the field href will be used (Default: { href: '' }).
 *
 * @returns {string} The updated element URL, either with UTM params found in the page URL or the element URL.
 */
function implementUtmParams(url, windowLocation = { href: '' }) {
  const utmPrefix = 'utm_';

  if (!url) {
    return '#';
  }

  const { url: internalUrl, fragment: internalFragment } = extractFragment(url);
  const { url: windowLocationUrl } = extractFragment(
    windowLocation && windowLocation.href ? windowLocation.href : '',
  );

  const [path, queryString] = internalUrl.split('?');
  const [, urlQueryString] = windowLocationUrl.split('?');
  const urlUTMparams = urlQueryString
    ? urlQueryString.split('&').filter((param) => param.startsWith(utmPrefix))
    : [];

  if (!urlQueryString || !urlUTMparams.length) {
    return url;
  }

  let finalQueryString = queryString || '';

  /* istanbul ignore next*/
  if (urlUTMparams.length) {
    const elementNoUTMparams =
      finalQueryString
        .split('&')
        .filter((param) => !param.startsWith(utmPrefix)) ?? [];
    if (
      elementNoUTMparams &&
      elementNoUTMparams.length &&
      urlUTMparams &&
      urlUTMparams.length
    ) {
      finalQueryString = [...elementNoUTMparams, ...urlUTMparams].join('&');
    } else if (elementNoUTMparams && elementNoUTMparams.length) {
      finalQueryString = [...elementNoUTMparams].join('&');
    } else if (urlUTMparams && urlUTMparams.length) {
      finalQueryString = [...urlUTMparams].join('&');
    }
  }

  return `${path}?${
    finalQueryString.charAt(0) === '&'
      ? finalQueryString.substring(1)
      : finalQueryString
  }${internalFragment}`;
}

module.exports = {
  extractFragment,
  getSearchParamsFrom,
  implementUtmParams,
};
